<script setup lang="ts">
/**
 * 消息列表 + 滚动跟随。
 *
 * **布局几何是硬约束，别"简化"**（原实现里记录过实测数据）：
 * 滚动层必须脱离文档流——外层 `relative min-h-0 flex-1 overflow-hidden` +
 * 内层 `absolute inset-0`。如果内层留在流内写成 `h-full`，
 * 高度就会依赖内容（父级是内容撑高的 flex 项），形成百分比高度回环：
 * 实测消息区从 654px 涨到 1402px，输入框被顶到屏幕外。
 *
 * **跟随策略**：只有当用户本来就贴着底部时才自动滚。
 * 他正在往上翻历史的时候，每来一个 delta 就把视图拽回底部，
 * 是流式聊天里最让人恼火的一类 bug。
 */

import { computed, ref, watch } from 'vue';

import { cn } from '../../lib/cn';
import type { ChatListItem, MessageViewModel } from '../../types';
import AppFooter from '../layout/AppFooter.vue';
import MessageBubble from './MessageBubble.vue';

/** 距底部 80px 内算"贴着底部"：留一点余量，触控板惯性滚动不会刚好差几像素。 */
const STICK_THRESHOLD_PX = 80;

const SUGGESTIONS: ReadonlyArray<string> = [
  '孩子写作业特别拖拉，每天都拖到十一点',
  '孩子一不顺心就摔东西、大喊大叫，我该怎么办',
  '怎么跟 13 岁的孩子谈手机使用时间',
];

const props = withDefaults(
  defineProps<{
    items: ChatListItem[];
    loading?: boolean;
    historyError?: string | null;
    onRegenerate?: (() => void) | undefined;
    onSuggestion?: ((text: string) => void) | undefined;
    resetKey?: string;
    className?: string;
  }>(),
  { loading: false, historyError: null, resetKey: 'new' },
);

const containerRef = ref<HTMLDivElement | null>(null);
const stick = ref(true);
const showJumpButton = ref(false);

const isEmpty = computed(() => !props.loading && props.items.length === 0);

function scrollToBottom(behavior: ScrollBehavior = 'auto'): void {
  const node = containerRef.value;
  if (node === null) return;
  node.scrollTo({ top: node.scrollHeight, behavior });
}

function updateStick(): void {
  const node = containerRef.value;
  if (node === null) return;
  const distance = node.scrollHeight - node.scrollTop - node.clientHeight;
  const next = distance <= STICK_THRESHOLD_PX;
  stick.value = next;
  showJumpButton.value = !next;
}

/** 本地乐观消息：内容与领域已知，其余元信息一律为空。 */
function localUserView(item: Extract<ChatListItem, { kind: 'local-user' }>): MessageViewModel {
  return {
    id: item.local.id,
    role: 'user',
    content: item.local.content,
    domain: item.local.domain,
    ageBand: item.local.ageBand,
    safetyFlag: null,
    estimated: null,
    latencyMs: null,
    upstreamModel: null,
    createdAt: null,
  };
}

watch(
  () => props.items,
  () => {
    if (stick.value) scrollToBottom();
  },
);

watch(
  () => props.resetKey,
  () => {
    // 会话切换：无条件吸底，并把"回到最新"收起来。
    stick.value = true;
    showJumpButton.value = false;
    scrollToBottom();
  },
  { flush: 'post' },
);
</script>

<template>
  <div :class="cn('relative min-h-0 flex-1 overflow-hidden', props.className)">
    <div class="absolute inset-0">
      <div
        ref="containerRef"
        class="no-scrollbar h-full overflow-y-auto overscroll-contain px-4 py-4"
        tabindex="0"
        role="log"
        aria-label="对话内容"
        @scroll.passive="updateStick"
      >
        <div class="mx-auto flex max-w-3xl flex-col gap-3">
          <p v-if="props.loading" class="py-6 text-center text-sm text-slate-500" role="status">
            正在加载会话内容…
          </p>

          <p
            v-if="props.historyError !== null"
            class="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-center text-xs text-amber-900"
            role="alert"
          >
            {{ props.historyError }}
          </p>

          <div v-if="isEmpty" class="py-10 text-center">
            <p class="text-sm text-slate-600">说说你正在遇到的情况，例如孩子的年龄、最近发生的一件事。</p>
            <p class="mt-1 text-xs text-slate-500">回答仅供参考，不构成诊断；紧急情况请拨打 12356。</p>

            <div v-if="props.onSuggestion !== undefined" class="mt-4 flex flex-wrap justify-center gap-2">
              <button
                v-for="text in SUGGESTIONS"
                :key="text"
                type="button"
                class="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700 hover:border-brand-300 hover:bg-brand-50"
                @click="props.onSuggestion?.(text)"
              >
                {{ text }}
              </button>
            </div>
          </div>

          <template v-for="item in props.items" :key="item.key">
            <MessageBubble v-if="item.kind === 'message'" :view="item.view" />
            <MessageBubble v-else-if="item.kind === 'local-user'" :view="localUserView(item)" />
            <!-- aria-live 用 polite 而不是 assertive：每个 delta 都打断读屏会变成噪声。 -->
            <div v-else aria-live="polite" aria-atomic="false">
              <MessageBubble :view="item.draft" :on-regenerate="props.onRegenerate" />
            </div>
          </template>

          <!-- 有消息时才渲染页脚：空会话已经有免责声明，再压一个完整页脚会吃掉第一屏。
               负外边距抵消父级的 px-4 py-4，让它通栏。 -->
          <AppFooter v-if="props.items.length > 0" class="-mx-4 -mb-4 mt-6" />
        </div>
      </div>
    </div>

    <button
      v-if="showJumpButton"
      type="button"
      class="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 shadow-md hover:bg-slate-50"
      @click="
        () => {
          stick = true;
          showJumpButton = false;
          scrollToBottom('smooth');
        }
      "
    >
      回到最新 ↓
    </button>
  </div>
</template>
